export interface UserEvent {
  eventType: string;
  userId: string;
  properties: Record<string, any>;
  timestamp: number;
}

export interface SessionData {
  userId: string;
  startTime: number;
  endTime?: number;
  pages: string[];
  events: UserEvent[];
}

export class AnalyticsService {
  private static instance: AnalyticsService;
  private events: UserEvent[] = [];
  private sessions: Map<string, SessionData> = new Map();
  private flushInterval: NodeJS.Timeout;

  private constructor() {
    this.flushInterval = setInterval(() => this.flush(), 300000); // 5 minutes
  }

  public static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  public trackEvent(userId: string, eventType: string, properties: Record<string, any> = {}): void {
    const event: UserEvent = {
      eventType,
      userId,
      properties,
      timestamp: Date.now()
    };

    this.events.push(event);
    this.updateSession(userId, event);
  }

  public startSession(userId: string): void {
    this.sessions.set(userId, {
      userId,
      startTime: Date.now(),
      pages: [],
      events: []
    });
  }

  public endSession(userId: string): void {
    const session = this.sessions.get(userId);
    if (session) {
      session.endTime = Date.now();
      this.trackEvent(userId, 'session_end', {
        duration: session.endTime - session.startTime,
        pageCount: session.pages.length
      });
    }
  }

  private updateSession(userId: string, event: UserEvent): void {
    const session = this.sessions.get(userId);
    if (session) {
      session.events.push(event);
      if (event.eventType === 'page_view') {
        session.pages.push(event.properties.page);
      }
    }
  }

  private async flush(): Promise<void> {
    if (this.events.length === 0) return;

    try {
      // In a real implementation, send to analytics service
      const payload = {
        events: this.events,
        timestamp: Date.now()
      };

      // Clear buffer after successful send
      this.events = [];
    } catch (error) {
      console.error('Failed to flush analytics data:', error);
    }
  }

  public destroy(): void {
    clearInterval(this.flushInterval);
  }
}